import { useQuery } from '@tanstack/react-query';
import { channelsApi } from '@/modules/channels/services/channelsApi';
import type { ChannelResponse, ExtendedStatus } from '@troia-v3/types';

/**
 * Status values that indicate a channel is disconnected or has connection issues
 */
const DISCONNECTED_STATUSES: ExtendedStatus[] = ['error', 'pending'];

export interface DisconnectedChannel {
  id: string;
  name: string;
  status: ExtendedStatus;
}

export interface UseDisconnectedChannelsResult {
  /** List of disconnected channels */
  disconnectedChannels: DisconnectedChannel[];
  /** Whether there are any disconnected channels */
  hasDisconnectedChannels: boolean;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Refetch function */
  refetch: () => void;
}

/**
 * Hook to monitor channel connection status
 * Fetches channels and filters those with disconnected status (error, inactive, suspended)
 *
 * @example
 * const { disconnectedChannels, hasDisconnectedChannels } = useDisconnectedChannels();
 *
 * if (hasDisconnectedChannels) {
 *   // Show warning widget
 * }
 */
export const useDisconnectedChannels = (): UseDisconnectedChannelsResult => {
  const { data: channels, isLoading, error, refetch } = useQuery({
    queryKey: ['channels', 'connection-status'],
    queryFn: () => channelsApi.getChannelsWithStatus(),
    staleTime: 30 * 1000, // 30 seconds - check frequently for connection issues
    refetchInterval: 60 * 1000, // Refetch every 60 seconds
    refetchOnWindowFocus: true,
    retry: 2,
  });

  const disconnectedChannels: DisconnectedChannel[] = (channels || [])
    .filter((channel: ChannelResponse) => DISCONNECTED_STATUSES.includes(channel.status))
    .map((channel: ChannelResponse) => ({
      id: channel.id,
      name: channel.name,
      status: channel.status,
    }));

  return {
    disconnectedChannels,
    hasDisconnectedChannels: disconnectedChannels.length > 0,
    isLoading,
    error: error as Error | null,
    refetch,
  };
};
